/**
 * 출퇴근 처리 Hook
 *
 * V150: 프론트엔드 직접 Firestore 쓰기 → Cloud Functions 호출로 변경
 * V187: 출퇴근 수정 요청도 Cloud Functions로 이관 (FE 직접 쓰기 제거)
 *
 * **변경 전:** addDoc(collection(getDb(), 'attendances'), {...})
 * **변경 후:** httpsCallable(functions, 'checkInAttendance')
 *
 * **규칙:**
 * - 1일 1회 출근 + 1회 퇴근만 허용
 * - 검증은 서버(Cloud Functions)에서 수행
 * - V187: 출퇴근 수정 요청 시 휴가 충돌/중복 요청 서버에서 검증
 */

import { useState } from 'react';
import { httpsCallable, FunctionsError } from 'firebase/functions';
import { Timestamp } from 'firebase/firestore';
import { getFunctions } from '../../firebase/utils';
import { Attendance, CheckInResult, CheckOutResult, AttendanceManualRequestInput } from '../types/attendance';
import { useCurrentUser } from './useCurrentUser';

/**
 * 출근 응답 타입
 */
interface CheckInResponse {
  success: boolean;
  attendanceId?: string;
  isLate?: boolean;
  checkInTime?: string;
  plannedStartTime?: string;
  plannedEndTime?: string;
  error?: string;
}

/**
 * 퇴근 응답 타입
 */
interface CheckOutResponse {
  success: boolean;
  attendanceId?: string;
  checkOutTime?: string;
  actualWorkMinutes?: number;
  overtimeMinutes?: number;
  error?: string;
}

/**
 * V187: 출퇴근 수정 요청 응답 타입
 */
interface CreateAttendanceManualRequestResponse {
  success: boolean;
  attendanceManualRequestId: string;
  approvalRequestId: string;
  approverId: string;
  approverName: string;
}

/**
 * V187: 출퇴근 수정 요청 결과 타입
 */
export interface AttendanceManualRequestResult {
  success: boolean;
  attendanceManualRequestId?: string;
  approvalRequestId?: string;
  error?: string;
}

/**
 * V194: 출퇴근 수기조정 취소 요청 입력 타입
 */
export interface AttendanceManualCancellationInput {
  attendanceManualRequestId: string;
  reason?: string;
}

/**
 * V194: 출퇴근 수기조정 취소 요청 응답 타입
 */
interface RequestAttendanceManualCancellationResponse {
  success: boolean;
  approvalRequestId?: string;
  approverId?: string;
  approverName?: string;
}

/**
 * V194: 출퇴근 수기조정 취소 요청 결과 타입
 */
export interface AttendanceManualCancellationResult {
  success: boolean;
  approvalRequestId?: string;
  error?: string;
}

export function useAttendanceMutations() {
  const { user } = useCurrentUser();
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  /**
   * 출근 처리 (V150: Cloud Functions 호출)
   */
  const checkIn = async (): Promise<CheckInResult> => {
    if (!user?.profile?.id) {
      return { success: false, error: '로그인이 필요합니다.' };
    }

    try {
      setIsProcessing(true);
      setError(null);

      const functions = getFunctions();
      const checkInFn = httpsCallable<void, CheckInResponse>(functions, 'checkInAttendance');

      const result = await checkInFn();

      if (result.data.success) {
        // 성공 시 프론트엔드용 Attendance 객체 생성
        const attendance: Attendance = {
          id: result.data.attendanceId!,
          employeeId: user.profile.id,
          employeeName: user.profile.displayName,
          date: new Date().toISOString().split('T')[0],
          checkInTime: Timestamp.fromDate(new Date(result.data.checkInTime!)),
          isLate: result.data.isLate || false,
          isAutoGenerated: false,
          plannedStartTime: result.data.plannedStartTime || undefined,
          plannedEndTime: result.data.plannedEndTime || undefined,
          actualWorkMinutes: 0,
          overtimeMinutes: 0,
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now(),
        };

        return { success: true, attendance };
      } else {
        return { success: false, error: result.data.error || '출근 처리 실패' };
      }
    } catch (err: any) {
      console.error('[V150] 출근 처리 실패:', err);
      setError(err);

      // Firebase Functions 에러 메시지 추출
      const errorMessage = err.message || '출근 처리 중 오류가 발생했습니다.';
      return { success: false, error: errorMessage };
    } finally {
      setIsProcessing(false);
    }
  };

  /**
   * 퇴근 처리 (V150: Cloud Functions 호출)
   */
  const checkOut = async (): Promise<CheckOutResult> => {
    if (!user?.profile?.id) {
      return { success: false, error: '로그인이 필요합니다.' };
    }

    try {
      setIsProcessing(true);
      setError(null);

      const functions = getFunctions();
      const checkOutFn = httpsCallable<void, CheckOutResponse>(functions, 'checkOutAttendance');

      const result = await checkOutFn();

      if (result.data.success) {
        // 성공 시 프론트엔드용 Attendance 객체 생성
        const attendance: Attendance = {
          id: result.data.attendanceId!,
          employeeId: user.profile.id,
          employeeName: user.profile.displayName,
          date: new Date().toISOString().split('T')[0],
          checkOutTime: Timestamp.fromDate(new Date(result.data.checkOutTime!)),
          isLate: false,
          isAutoGenerated: false,
          actualWorkMinutes: result.data.actualWorkMinutes || 0,
          overtimeMinutes: result.data.overtimeMinutes || 0,
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now(),
        };

        return { success: true, attendance };
      } else {
        return { success: false, error: result.data.error || '퇴근 처리 실패' };
      }
    } catch (err: any) {
      console.error('[V150] 퇴근 처리 실패:', err);
      setError(err);

      // Firebase Functions 에러 메시지 추출
      const errorMessage = err.message || '퇴근 처리 중 오류가 발생했습니다.';
      return { success: false, error: errorMessage };
    } finally {
      setIsProcessing(false);
    }
  };

  /**
   * V187: 출퇴근 수정 요청 (Cloud Functions 호출)
   *
   * **V187 변경 사항:**
   * - FE 직접 Firestore 쓰기 제거
   * - Cloud Function `createAttendanceManualRequest` 호출
   * - 서버에서 휴가 충돌, 중복 요청 검증 수행
   *
   * **에러 처리:**
   * - 휴가 충돌: "해당 날짜에 휴가가 있어 수정 요청 불가"
   * - 중복 요청: "이미 처리 대기 중인 요청 존재"
   * - 승인자 없음: "조직에 승인자가 설정되지 않음"
   */
  const requestAttendanceManual = async (input: AttendanceManualRequestInput): Promise<AttendanceManualRequestResult> => {
    if (!user?.profile?.id) {
      return { success: false, error: '로그인이 필요합니다.' };
    }

    try {
      setIsProcessing(true);
      setError(null);

      const functions = getFunctions();
      const createAttendanceManualRequestFn = httpsCallable<
        {
          date: string;
          requestedCheckInTime?: string;
          requestedCheckOutTime?: string;
          /** V66: 0=당일, 1=익일 퇴근 */
          checkOutDateOffset?: 0 | 1;
          reason: string;
        },
        CreateAttendanceManualRequestResponse
      >(functions, 'createAttendanceManualRequest');

      // V60.x: "HH:mm" 문자열을 그대로 전달 (toISOString UTC 변환 제거)
      // V66: 익일 퇴근(checkOutDateOffset=1)이면 함께 전달
      const result = await createAttendanceManualRequestFn({
        date: input.date,
        requestedCheckInTime: input.requestedCheckInTime,
        requestedCheckOutTime: input.requestedCheckOutTime,
        ...(input.checkOutDateOffset === 1 ? { checkOutDateOffset: 1 } : {}),
        reason: input.reason,
      });

      if (result.data.success) {
        return {
          success: true,
          attendanceManualRequestId: result.data.attendanceManualRequestId,
          approvalRequestId: result.data.approvalRequestId,
        };
      } else {
        return { success: false, error: '출퇴근 수정 요청 생성 실패' };
      }
    } catch (err: any) {
      setError(err);

      // Firebase Functions 에러 메시지 추출 및 사용자 친화적 메시지 변환
      let errorMessage = '출퇴근 수정 요청 중 오류가 발생했습니다.';

      if (err.code === 'functions/failed-precondition') {
        // 휴가 충돌 또는 승인자 없음
        errorMessage = err.message;
      } else if (err.code === 'functions/already-exists') {
        // 중복 요청
        errorMessage = err.message;
      } else if (err.code === 'functions/invalid-argument') {
        // 입력값 검증 실패
        errorMessage = err.message;
      } else if (err.message) {
        errorMessage = err.message;
      }

      return { success: false, error: errorMessage };
    } finally {
      setIsProcessing(false);
    }
  };

  /**
   * V194: 승인된 출퇴근 수기조정 취소 요청 (Cloud Functions 호출)
   *
   * **V194 기능:**
   * - 승인 완료(approved)된 출퇴근 수기조정 요청에 대해 취소 요청
   * - approvalRequest(type='attendanceManualCancel') 생성
   * - 승인자가 승인하면 attendances 원복(rollback) 수행
   *
   * **제약:**
   * - 본인 요청만 취소 가능
   * - approved 상태만 취소 가능
   * - 이미 cancelPending이면 중복 요청 불가
   */
  const requestAttendanceManualCancellation = async (
    input: AttendanceManualCancellationInput
  ): Promise<AttendanceManualCancellationResult> => {
    if (!user?.profile?.id) {
      return { success: false, error: '로그인이 필요합니다.' };
    }

    try {
      setIsProcessing(true);
      setError(null);

      const functions = getFunctions();
      const requestCancellationFn = httpsCallable<
        {
          attendanceManualRequestId: string;
          reason?: string;
        },
        RequestAttendanceManualCancellationResponse
      >(functions, 'requestAttendanceManualCancellation');

      const result = await requestCancellationFn({
        attendanceManualRequestId: input.attendanceManualRequestId,
        reason: input.reason?.trim() || undefined,
      });

      if (result.data.success) {
        return {
          success: true,
          approvalRequestId: result.data.approvalRequestId,
        };
      } else {
        return { success: false, error: '출퇴근 수기조정 취소 요청 실패' };
      }
    } catch (err: any) {
      console.error('[V194] 출퇴근 수기조정 취소 요청 실패:', err);
      setError(err);

      // Firebase Functions 에러 메시지 추출
      let errorMessage = '출퇴근 수기조정 취소 요청 중 오류가 발생했습니다.';

      if (err.code === 'functions/failed-precondition') {
        errorMessage = err.message;
      } else if (err.code === 'functions/permission-denied') {
        errorMessage = err.message;
      } else if (err.code === 'functions/not-found') {
        errorMessage = err.message;
      } else if (err.message) {
        errorMessage = err.message;
      }

      return { success: false, error: errorMessage };
    } finally {
      setIsProcessing(false);
    }
  };

  return {
    checkIn,
    checkOut,
    requestAttendanceManual,
    requestAttendanceManualCancellation,  // V194
    isProcessing,
    error,
  };
}
